import { useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { v4 } from 'uuid';
import { useWebSocket } from './useWebSocket';
import {
  addEmailMessage,
  addUnreadMessage,
} from 'store/projectEmail/projectEmailSlice';

/**
 * Custom hook for managing project email WebSocket connection with automatic resubscription
 * @param {Object} user - Current user object
 * @returns {Object} - { emailWebsocketRef, subscribeToProjects }
 */
export const useProjectEmailWebSocket = (user) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const emailProjectKeysRef = useRef([]);

  const subscribeToProjectEmails = (ws, projectKeys) => {
    if (!ws || !projectKeys.length) return;

    projectKeys.forEach((projectKey) => {
      const subscribeMessage = {
        newConnection: {
          id: v4(),
          userId: user.id,
          projectKey,
        },
      };
      ws.send(JSON.stringify(subscribeMessage));
    });
  };

  const { wsRef: emailWebsocketRef } = useWebSocket({
    url: process.env.REACT_APP_PROJECT_EMAIL_WEBSOCKET_URL,
    enabled: !!user?.id,
    onMessage: (event) => {
      try {
        const message = JSON.parse(event.data);

        // Always increment unread count
        dispatch(addUnreadMessage({ projectKey: message.projectKey }));

        // Only add to message list if user is viewing this project's email
        const currentPath = history.location.pathname;
        const isViewingProjectEmail = currentPath.includes(
          `/project/${message.projectKey}/email`
        );

        if (isViewingProjectEmail) {
          dispatch(addEmailMessage({ emailMessage: message }));
        }
      } catch (error) {
        console.error('Error parsing project email WebSocket message:', error);
      }
    },
    onOpen: (event, ws) => {
      window.emailWebsocketRef = emailWebsocketRef;

      // Resubscribe to all projects on reconnect
      if (emailProjectKeysRef.current.length > 0) {
        subscribeToProjectEmails(ws, emailProjectKeysRef.current);
      }
    },
    onClose: (event) => {
      delete window.emailWebsocketRef;
    },
    onError: (error) => {
      console.error('Project email WebSocket error:', error);
    },
  });

  const subscribeToProjects = (projectKeys) => {
    // Store project keys for resubscription on reconnect
    emailProjectKeysRef.current = projectKeys;

    if (emailWebsocketRef.current && projectKeys.length > 0) {
      const ws = emailWebsocketRef.current;

      if (ws.readyState === WebSocket.OPEN) {
        subscribeToProjectEmails(ws, projectKeys);
      } else if (ws.readyState === WebSocket.CONNECTING) {
        ws.addEventListener(
          'open',
          () => {
            subscribeToProjectEmails(ws, projectKeys);
          },
          { once: true }
        );
      }
    }
  };

  return {
    emailWebsocketRef,
    subscribeToProjects,
  };
};
